import CachedIcon from "@mui/icons-material/CachedRounded";
import { Button, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import adminApi from "../../../api/adminApi";
import type { DataImportInfo } from "../../../api/types/dataImportTypes";
import useClientContext from "../../../context/useClientContext";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import DeleteDataImportDialog from "./DeleteDataImportDialog";
import ImportDataGrid from "./grid/ImportDataGrid";
import { ImportDataContextProvider } from "./ImportDataContext";
import ImportDataPageActions from "./ImportDataPageActions";

export interface DialogState {
  importToDelete?: DataImportInfo;
}

const ImportDataPage = () => {
  usePageTitle("Data Import");

  const { hasPermissions } = useClientContext();

  const [dialogState, setDialogState] = useState<DialogState>({});

  const [dataImportOptions] = useFetch(adminApi.getDataImportOptions);

  const { gridApiRef, dataSource, totalRows, isLoading, isRefreshing, refreshDataSource } = useGridDataSourceWithRange(
    adminApi.searchDataImports,
    {}
  );

  const handleDeleteImport = useCallback((importToDelete: DataImportInfo) => {
    setDialogState({ importToDelete });
  }, []);

  const handleImportDeleted = useCallback(() => {
    setDialogState({ importToDelete: undefined });
    refreshDataSource();
  }, [refreshDataSource]);

  return (
    <ImportDataContextProvider refreshData={refreshDataSource} onDeleteImport={handleDeleteImport}>
      <GeneralPageHeader title="Data Import">
        <ImportDataPageActions
          hasAccessToCsvImport={Boolean(dataImportOptions?.length)}
          hasAccessToPassthroughImport={hasPermissions(["ManageInvestorRelationsDataImports", "ManageFundStructure"])}
        />
      </GeneralPageHeader>
      <Stack spacing={2.5} sx={{ py: 2.5, px: 3, height: "100%" }}>
        <Stack spacing={2} direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
          <RecordCounter records={totalRows ?? 0} hide={isLoading} />
          <Button
            color="secondary"
            variant="outlined"
            startIcon={<CachedIcon />}
            onClick={refreshDataSource}
            disabled={isLoading}
            loading={isRefreshing}
          >
            Refresh
          </Button>
        </Stack>

        <ImportDataGrid dataSource={dataSource} apiRef={gridApiRef} />
      </Stack>
      {dialogState.importToDelete !== undefined && (
        <DeleteDataImportDialog
          dataImport={dialogState.importToDelete}
          onClose={() => setDialogState({ importToDelete: undefined })}
          onConfirm={handleImportDeleted}
        />
      )}
    </ImportDataContextProvider>
  );
};

export default ImportDataPage;
